/**
 * Design tokens — the single source of truth, verbatim from the design handoff.
 *
 * Platform-agnostic on purpose: apps/web emits these as CSS custom properties,
 * and a future Expo app consumes the same constants directly. Changing a colour
 * here changes it on every platform.
 */

export const colors = {
  bg: '#fbfaf8',
  card: '#ffffff',
  ink: '#181720',
  ink2: '#3b3947',
  muted: '#6f6c7b',
  line: 'rgba(24,23,32,.10)',
  line2: 'rgba(24,23,32,.06)',

  // Accent — deep indigo
  // Graphite accent, matching the prototype's rendered theme. Mirrors
  // apps/web/src/styles/tokens.css — change both together.
  acc: 'oklch(0.34 0.03 267)',
  accInk: 'oklch(0.26 0.03 267)',
  accSoft: 'oklch(0.963 0.006 267)',
  accLine: 'oklch(0.875 0.014 267)',

  // Amber — warnings / in-progress
  amber: 'oklch(0.62 0.13 68)',
  amberSoft: 'oklch(0.965 0.03 78)',
  amberLine: 'oklch(0.88 0.07 78)',

  // Green — success / signal
  green: 'oklch(0.52 0.11 155)',
  greenSoft: 'oklch(0.965 0.028 155)',
  greenLine: 'oklch(0.87 0.06 155)',

  // Error
  error: 'oklch(0.55 0.19 25)',
  errorLine: 'oklch(0.62 0.17 25)',

  // Section tints
  tint: '#f7f5f2',
  gutter: '#f1efeb',
  hover: '#faf9f7',
} as const;

export const fonts = {
  ui: "'Instrument Sans', system-ui, -apple-system, sans-serif",
  mono: "'IBM Plex Mono', ui-monospace, monospace",
} as const;

export const radii = {
  pill: '20px',
  card: '16px',
  cardSm: '14px',
  control: '10px',
  sm: '8px',
  xs: '7px',
  logo: '6px',
} as const;

export const shadows = {
  card: '0 1px 2px rgba(24,23,32,.05)',
  button: '0 1px 2px rgba(24,23,32,.16)',
  lift: '0 8px 20px rgba(24,23,32,.09)',
  toolbar: '0 10px 30px rgba(24,23,32,.28)',
} as const;

export const layout = {
  landingMax: '1080px',
  dashboardMax: '960px',
  authMax: '420px',
  mobileShell: '402px',
} as const;

/** Maps an idea status to its badge colour set. */
export const statusTokens = {
  report_ready: { fg: colors.green, bg: colors.greenSoft, line: colors.greenLine },
  awaiting_refinement: { fg: colors.accInk, bg: colors.accSoft, line: colors.accLine },
  in_research: { fg: colors.amber, bg: colors.amberSoft, line: colors.amberLine },
  draft: { fg: colors.muted, bg: colors.gutter, line: colors.line },
} as const;

/** Human-readable status labels used on dashboard badges. */
export const statusLabels = {
  report_ready: 'REPORT READY',
  awaiting_refinement: 'AWAITING REFINEMENT',
  in_research: 'IN RESEARCH',
  draft: 'DRAFT',
} as const;
